/**
 * UserDetail — detalle inline de un usuario (reemplaza al ViewUserModal).
 *
 * Mismo patrón que el detalle del hub Empresas: al clickear una fila la lista
 * se reemplaza por esta vista (banda de identidad + actividad + secciones),
 * con deep-link ?user=<id> y "Volver" para regresar a la lista.
 *
 * Solo presentación + actividad: las ACCIONES (editar, bloquear, eliminar,
 * marcar vendedor…) reusan los handlers/modales del módulo padre, que llegan
 * por props. La "Actividad de entregas" usa el heatmap compartido:
 *   - clientes/comercios → por companyId (anclado a deliveredAt)
 *   - choferes           → por driverId (entregados por semana de creación)
 *   - gestión interna    → sin actividad (no opera pedidos)
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Banknote, Building2, ExternalLink, FileText, Fingerprint,
  Landmark, Lock, MessageCircle, Pencil, Pause, Play, Send, Trash2, Truck,
  Unlock, UserCheck, UserCircle, UserCog,
} from "lucide-react";
import { Avatar } from "../../../components/ui/Avatar";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { SectionCard } from "../../../components/ui/SectionCard";
import { cn } from "../../../utils/cn";
import { safeFileUrl } from "../../../utils/safeUrl";
import EntregasHeatmap from "../../../components/deliveryActivity/EntregasHeatmap";
import { driverVehicleLabel } from "../../../utils/driverVehicleTypes";
import { formatDate, formatRelative } from "../../../utils/formatters";
import { teamStatus, roleTone, initialsOf, canBeSeller, humanizeRole } from "./usersConfig";

// Un objeto "tiene contenido" si al menos un valor es no-vacío (los subdocs
// bankData/dniData/vehicleData suelen venir como {} o con strings vacíos).
const hasContent = (obj) =>
  !!obj && Object.values(obj).some((v) => v != null && String(v).trim() !== "");

// Acciones sobre un teléfono: abrir su conversación en la mesa de ayuda
// (deep-link ?phone=, resuelto por el inbox) o escribirle directo por
// WhatsApp (mismo patrón wa.me que evidenceView). Solo dígitos en ambos.
const phoneDigits = (phone) => String(phone || "").replace(/\D/g, "");
const helpdeskHref = (phone) => `/admin/helpdesk?phone=${phoneDigits(phone)}`;
const waHref = (phone) => `https://wa.me/${phoneDigits(phone)}`;

// Tile compacto micro-label + valor (patrón "Condiciones del contrato" del
// detalle de Empresas) para grillas densas. break-words, no truncate: acá van
// direcciones y números de cuenta que no pueden recortarse en silencio.
function FieldTile({ label, value, to, title, mono, className }) {
  return (
    <div className={cn("min-w-0 rounded-lg bg-bg-muted px-3 py-2", className)}>
      <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-fg-muted">
        {label}
      </div>
      <div className={cn("mt-0.5 text-sm text-fg break-words", mono && "font-mono")}>
        {value && to ? (
          <Link
            to={to}
            title={title}
            className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          >
            {value}
          </Link>
        ) : (
          value || "—"
        )}
      </div>
    </div>
  );
}

// Fila con chip de ícono + micro-label + valor (patrón ContactItem del detalle
// de Empresas) para secciones cortas de 2-4 campos.
function InfoTile({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-muted text-fg-muted">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-fg-muted">
          {label}
        </div>
        <div className={cn("mt-0.5 text-sm text-fg break-words", mono && "font-mono")}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

// Documento adjunto del chofer: link para abrir + vista previa embebida. La URL llega
// de la API y se filtra el esquema antes de usarla (un `javascript:` en el href o en el
// src del iframe correría con el origen del panel).
function DocBlock({ label, url }) {
  const { t } = useTranslation();
  const href = safeFileUrl(url);
  const isPdf = href?.toLowerCase().endsWith(".pdf");
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-fg">{label}</span>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          >
            {t("Abrir en pestaña nueva")}
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        ) : (
          <span className="text-sm text-fg-subtle">{t("No disponible")}</span>
        )}
      </div>
      {href && (
        <div className="overflow-hidden rounded-lg border border-border bg-bg-muted">
          {isPdf ? (
            <iframe src={href} title={label} className="h-64 w-full" />
          ) : (
            <img src={href} alt={label} className="dark-safe-img max-h-64 w-full object-contain" />
          )}
        </div>
      )}
    </div>
  );
}

export default function UserDetail({
  user: u,
  seg,
  isPendingDriver,
  canManageTeam,
  busyId,
  onBack,
  onEdit,
  onBlock,
  onDelete,
  onEnable,
  onTeamToggle,
  onTeamResend,
  onToggleSeller,
  onToggleSalesSupervisor,
}) {
  const { t } = useTranslation();
  const blocked = u.active === false;
  const bank = u?.bankData || {};
  const dni = u?.dniData || {};
  const vehicle = u?.vehicleData || {};
  const company = u?.companyId || null;
  const status = teamStatus(u);

  const countryLabel =
    typeof u?.country === "string" ? u.country : u?.country?.name || null;

  const fullAddress = [u.address, u.addressDetail].filter(Boolean).join(" ");
  const docsLoaded = [u.driveLicense, u.antecedentDocument].filter(Boolean).length;

  return (
    <div className="w-full min-w-0 space-y-4 anim-fade-in-up">
      {/* Banda de identidad */}
      <div className="rounded-lg border border-border bg-bg p-4 shadow-card">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={onBack} iconLeft={<ArrowLeft className="w-4 h-4" />}>
              {t("Volver")}
            </Button>
            <div className="h-6 w-px bg-border shrink-0" aria-hidden="true" />
            <Avatar size="lg" tone={blocked ? "neutral" : "brand"} initials={initialsOf(u.name, u.lastName)} alt={`${u.name || ""} ${u.lastName || ""}`} />
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-fg truncate">
                {u.name} {u.lastName}
              </h1>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
                <span className="truncate">{u.email || "—"}</span>
                {u.phone && (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <Link
                      to={helpdeskHref(u.phone)}
                      title={t("Abrir chat en la mesa de ayuda")}
                      className="hover:text-fg hover:underline"
                    >
                      {u.phone}
                    </Link>
                    <a
                      href={waHref(u.phone)}
                      target="_blank"
                      rel="noreferrer"
                      title={t("Escribir por WhatsApp")}
                      aria-label={t("Escribir por WhatsApp")}
                      className="text-status-success hover:opacity-80"
                    >
                      <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                    </a>
                  </span>
                )}
                {seg === "clientes" && company?.name && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <Building2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">{company.name}</span>
                  </span>
                )}
                {u.createdAt && (
                  <span className="whitespace-nowrap">
                    {t("Registro")}: {formatRelative(u.createdAt)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Badge tone={roleTone(u?.role?.name)}>{humanizeRole(u?.role?.name_public || u?.role?.name || "—")}</Badge>
            {u?.customRole?.name && <Badge tone="info">{u.customRole.name}</Badge>}
            {seg === "choferes" && u.vehicleType && (
              <Badge tone="neutral">{driverVehicleLabel(u.vehicleType, t)}</Badge>
            )}
            {u.isSeller && canBeSeller(u) && <Badge tone="info">{t("Vendedor")}</Badge>}
            {u.isSalesSupervisor && canBeSeller(u) && (
              <Badge tone="warning">{t("Supervisor de ventas")}</Badge>
            )}
            {isPendingDriver ? (
              // Un autoregistro sin aprobar no está "Bloqueado": está pendiente
              // (mismo criterio que la fila de la cola "Por aceptar").
              <Badge tone="warning" dot>{t("Pendiente")}</Badge>
            ) : seg === "interna" ? (
              status === "pending" ? (
                <Badge tone="warning" dot>{t("Pendiente")}</Badge>
              ) : status === "inactive" ? (
                <Badge tone="neutral" dot>{t("Inactivo")}</Badge>
              ) : (
                <Badge tone="success" dot>{t("Activo")}</Badge>
              )
            ) : (
              <Badge tone={blocked ? "neutral" : "success"} dot>
                {blocked ? t("Bloqueado") : t("Activo")}
              </Badge>
            )}
          </div>
        </div>

        {/* Acciones: mismos handlers/modales del módulo (nada nuevo de backend). */}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button variant="outline" size="sm" onClick={() => onEdit(u)} iconLeft={<Pencil className="w-4 h-4" />}>
            {t("Editar")}
          </Button>
          {isPendingDriver ? (
            <Button size="sm" onClick={() => onEnable(u, "accept")} iconLeft={<UserCheck className="w-4 h-4" />}>
              {t("Aceptar repartidor")}
            </Button>
          ) : seg === "interna" && canManageTeam ? (
            <>
              {status === "pending" && (
                <Button variant="outline" size="sm" disabled={busyId === u._id} onClick={() => onTeamResend(u)} iconLeft={<Send className="w-4 h-4" />}>
                  {t("Reenviar invitación")}
                </Button>
              )}
              <Button variant="outline" size="sm" disabled={busyId === u._id} onClick={() => onTeamToggle(u)} iconLeft={u.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}>
                {u.active ? t("Desactivar") : t("Activar")}
              </Button>
            </>
          ) : blocked ? (
            <Button
              variant="outline"
              size="sm"
              className="border-status-success/40 text-status-success hover:bg-status-success-soft"
              onClick={() => onEnable(u, "unblock")}
              iconLeft={<Unlock className="w-4 h-4" />}
            >
              {t("Desbloquear")}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="border-status-warning/40 text-status-warning hover:bg-status-warning-soft"
              onClick={() => onBlock(u)}
              iconLeft={<Lock className="w-4 h-4" />}
            >
              {t("Bloquear")}
            </Button>
          )}
          {/* Toggles de vendedor/supervisor: solo si el contenedor los pasa
              (Cuentas del hub Empresas sí; UsersModule no los expone). */}
          {canBeSeller(u) && onToggleSeller && onToggleSalesSupervisor && (
            <>
              <Button variant="ghost" size="sm" onClick={() => onToggleSeller(u)} iconLeft={<Banknote className="w-4 h-4" />}>
                {u.isSeller ? t("Quitar vendedor") : t("Marcar como vendedor")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onToggleSalesSupervisor(u)} iconLeft={<UserCog className="w-4 h-4" />}>
                {u.isSalesSupervisor ? t("Quitar supervisor de ventas") : t("Marcar como supervisor de ventas")}
              </Button>
            </>
          )}
          {!isPendingDriver && (
            <Button
              variant="ghost"
              size="sm"
              className="text-status-error hover:bg-status-error-soft ml-auto"
              onClick={() => onDelete(u)}
              iconLeft={<Trash2 className="w-4 h-4" />}
            >
              {t("Eliminar")}
            </Button>
          )}
        </div>
      </div>

      {/* Actividad de entregas: comercio → por empresa; chofer → por asignación. */}
      {seg === "clientes" && company?._id && (
        <EntregasHeatmap
          companyId={company._id}
          subtitle={t("Pedidos entregados del comercio · últimos 12 meses")}
        />
      )}
      {seg === "choferes" && !isPendingDriver && (
        <EntregasHeatmap
          driverId={u._id}
          subtitle={t("Pedidos entregados por el chofer · últimos 12 meses")}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 items-start">
        <SectionCard
          tone="bg"
          icon={<UserCircle className="w-4 h-4" />}
          title={t("Datos personales")}
          bodyClassName="grid grid-cols-1 sm:grid-cols-2 gap-2"
        >
          <FieldTile label={t("Correo")} value={u.email} />
          <FieldTile label={t("Teléfono")} value={u.phone} to={helpdeskHref(u.phone)} title={t("Abrir chat en la mesa de ayuda")} />
          <FieldTile label={t("Contacto adicional")} value={u.aditionalPhone} to={helpdeskHref(u.aditionalPhone)} title={t("Abrir chat en la mesa de ayuda")} />
          <FieldTile label={t("RUT")} value={u.rut} mono />
          <FieldTile label={t("Dirección")} value={fullAddress} className="sm:col-span-2" />
          <FieldTile label={t("Ciudad/Comuna")} value={u.municipality} />
          <FieldTile label={t("Región/Provincia")} value={u.state} />
          <FieldTile label={t("País")} value={countryLabel} />
          <FieldTile label={t("Fecha de nacimiento")} value={u.birthDate ? formatDate(u.birthDate?.$date || u.birthDate) : null} />
        </SectionCard>

        <div className="space-y-4 min-w-0">
          {seg === "clientes" && company && (
            <SectionCard
              tone="bg"
              icon={<Building2 className="w-4 h-4" />}
              title={t("Comercio")}
              bodyClassName="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <InfoTile icon={Building2} label={t("Nombre")} value={company.name} />
              <InfoTile icon={FileText} label={t("RUT")} value={company.businessIdentifier} mono />
            </SectionCard>
          )}

          {hasContent(dni) && (
            <SectionCard
              tone="bg"
              icon={<Fingerprint className="w-4 h-4" />}
              title={t("Identificación")}
              bodyClassName="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <InfoTile icon={FileText} label={t("Tipo documento")} value={dni.dniType} />
              <InfoTile icon={Fingerprint} label={t("Número documento")} value={dni.dniNumber} mono />
            </SectionCard>
          )}

          {hasContent(bank) && (
            <SectionCard
              tone="bg"
              icon={<Landmark className="w-4 h-4" />}
              title={t("Datos bancarios")}
              subtitle={u.isSeller ? t("Cuenta para el pago de ventas del vendedor.") : null}
              bodyClassName="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <FieldTile label={t("Nombre")} value={bank.name} className="sm:col-span-2" />
              <FieldTile label={t("Correo")} value={bank.email} />
              <FieldTile label={t("RUT")} value={bank.rut} mono />
              <FieldTile label={t("Banco")} value={bank.bank} />
              <FieldTile label={t("Tipo de cuenta")} value={bank.accountType} />
              <FieldTile label={t("Número de cuenta")} value={bank.accountNumber} mono className="sm:col-span-2" />
            </SectionCard>
          )}
        </div>
      </div>

      {/* Vehículo y documentos fusionados (patrón ficha del mockup): línea de
          vehículo + estado "N/2 documentos cargados" (presencia, NO vigencia:
          el backend no expone vencimientos) + previews. Full-width para no
          estrangular los iframes/imágenes de los documentos. */}
      {seg === "choferes" && (
        <SectionCard
          tone="bg"
          icon={<Truck className="w-4 h-4" />}
          title={t("Vehículo y documentos")}
        >
          <div className="space-y-1.5">
            <div className="text-sm text-fg">
              {driverVehicleLabel(u?.vehicleType ?? vehicle.vehicleType, t)}
              {vehicle.vehiclePlate && (
                <>
                  {" · "}
                  <span className="font-mono text-fg-muted">{vehicle.vehiclePlate}</span>
                </>
              )}
            </div>
            <div
              className={cn(
                "text-sm font-medium",
                docsLoaded === 2 ? "text-status-success" : "text-status-warning",
              )}
            >
              {t("{{n}}/2 documentos cargados", { n: docsLoaded })}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DocBlock label={t("Licencia de conducir")} url={u.driveLicense} />
            <DocBlock label={t("Certificado de antecedentes")} url={u.antecedentDocument} />
          </div>
        </SectionCard>
      )}
    </div>
  );
}
